/**
 * Instagram-style multi-image carousel (up to 4 images).
 * - Chevron arrow overlays on hover (desktop)
 * - Dot indicators at bottom
 * - Keyboard: ←/→ arrows
 * - Touch: horizontal swipe on mobile web
 *
 * Backward-compatible: renders as a single fixed image if only 1 image
 * is passed. Handles both new (`image_urls`) and legacy (`image_url`)
 * post shapes — parent should pass a normalised array.
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

export default function PostImageCarousel({
    images,
    alt = "",
    maxHeightClass = "max-h-[480px]",
    onImageClick,
    testId,
}) {
    const [index, setIndex] = useState(0);
    const containerRef = useRef(null);
    const touchStartX = useRef(null);

    const list = Array.isArray(images) ? images.filter(Boolean) : [];
    const count = list.length;

    const goPrev = useCallback(() => setIndex((i) => (i - 1 + count) % count), [count]);
    const goNext = useCallback(() => setIndex((i) => (i + 1) % count), [count]);

    // Keyboard nav (only when the carousel is focused / hovered).
    useEffect(() => {
        if (count <= 1) return undefined;
        const el = containerRef.current;
        if (!el) return undefined;
        const onKey = (e) => {
            if (!el.matches(":hover") && document.activeElement !== el) return;
            if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
            if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [count, goPrev, goNext]);

    if (count === 0) return null;

    const single = count === 1;

    return (
        <div
            ref={containerRef}
            className="relative group rounded-xl border border-border overflow-hidden bg-muted/30 focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/50"
            tabIndex={count > 1 ? 0 : -1}
            data-testid={testId}
            onTouchStart={(e) => { touchStartX.current = e.touches[0]?.clientX ?? null; }}
            onTouchEnd={(e) => {
                if (touchStartX.current == null || count <= 1) return;
                const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
                if (Math.abs(dx) > 40) (dx < 0 ? goNext : goPrev)();
                touchStartX.current = null;
            }}
        >
            <img
                src={list[index]}
                alt={count > 1 ? `${alt} — image ${index + 1} of ${count}` : alt}
                loading="lazy"
                className={`w-full ${maxHeightClass} object-contain mx-auto transition-opacity duration-200`}
                data-testid={testId ? `${testId}-img-${index}` : undefined}
                onClick={onImageClick}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
            />

            {/* Arrow overlays — hidden by default, shown on hover for desktop */}
            {!single && (
                <>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); goPrev(); }}
                        className="absolute left-2 top-1/2 -translate-y-1/2 grid place-items-center h-9 w-9 rounded-full bg-black/40 hover:bg-black/70 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity backdrop-blur-sm"
                        aria-label="Previous image"
                        data-testid={testId ? `${testId}-prev` : undefined}
                    >
                        <CaretLeft weight="bold" size={18} />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); goNext(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center h-9 w-9 rounded-full bg-black/40 hover:bg-black/70 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity backdrop-blur-sm"
                        aria-label="Next image"
                        data-testid={testId ? `${testId}-next` : undefined}
                    >
                        <CaretRight weight="bold" size={18} />
                    </button>

                    {/* Dot indicators */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 px-2 py-1 rounded-full bg-black/30 backdrop-blur-sm">
                        {list.map((_, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setIndex(i); }}
                                className={`h-1.5 rounded-full transition-all ${i === index ? "w-4 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"}`}
                                aria-label={`Go to image ${i + 1}`}
                            />
                        ))}
                    </div>

                    {/* Counter pill (top-right) */}
                    <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/50 text-white text-[11px] font-medium backdrop-blur-sm select-none">
                        {index + 1}/{count}
                    </div>
                </>
            )}
        </div>
    );
}
